import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {NavigationEnd, Router, UrlTree} from '@angular/router';
import {merge, Observable, of} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {TranslateModule} from '@ngx-translate/core';
import {AppVersionComponent} from '@common-module/app-components/app-version/app-version.component';
import {AuthUserChipComponent} from '@common-module/app-components/auth-user-chip/auth-user-chip.component';
import {AuthUserNameComponent} from '@common-module/app-components/auth-user-name/auth-user-name.component';
import {HeaderNavComponent} from '@common-module/app-components/header-nav/header-nav.component';
import {HeaderNavItem} from '@common-module/app-components/header-nav/header-nav-item';
import {LogoTitleComponent} from '@common-module/app-components/logo-header/logo-title.component';
import {
  ChangePasswordDialogComponent
} from '@common-module/app-dialog/change-password-dialog/change-password-dialog.component';
import {SettingsDialogComponent} from '@common-module/app-dialog/settings-dialog/settings-dialog.component';
import {GlobalStyleClass} from '@common-module/app-enums/global-style-class.enum';
import {Tab} from '@common-module/app-enums/tab';
import {AuthUserService} from '@common-module/app-services/auth-user.service';
import {ViewBreakpointService, ViewSize} from '@common-module/app-services/view-breakpoint.service';
import {MaterialModule} from '@common-module/material.module';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['../../../../../common-module/src/lib/app-styles/header-shell.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    TranslateModule,
    MaterialModule,
    LogoTitleComponent,
    HeaderNavComponent,
    AuthUserChipComponent,
    AuthUserNameComponent
  ]
})
export class HeaderComponent implements OnInit {

  private static readonly BASE_TABS: HeaderNavItem[] = [
    {value: Tab.USERS, icon: 'supervisor_account', text: 'term.users'},
    {value: Tab.UNITS, icon: 'near_me', text: 'term.units'},
    {value: Tab.TEMPLATES, icon: 'library_books', text: 'term.templates'},
    {value: Tab.RETRANSLATORS, icon: 'open_in_new', text: 'term.retranslators'}
  ];

  private static readonly DEALER_TAB: HeaderNavItem =
    {value: Tab.BRAND, icon: 'camera', text: 'term.branding'};

  public activeTab$: Observable<string>;
  public navItems$: Observable<HeaderNavItem[]>;
  public size$: Observable<ViewSize>;

  constructor(private authUserService: AuthUserService,
              private dialog: MatDialog,
              private router: Router,
              private viewBreakpointService: ViewBreakpointService) {
    this.activeTab$ = merge(
      of(HeaderComponent.toTab(this.router.parseUrl(this.router.url))),
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map((event: NavigationEnd) => HeaderComponent.toTab(this.router.parseUrl(event.urlAfterRedirects)))));
  }

  ngOnInit(): void {
    this.size$ = this.viewBreakpointService.size$;
    this.navItems$ = this.authUserService.currentUser$.pipe(
      map(user => user?.isDealer()
        ? [...HeaderComponent.BASE_TABS, HeaderComponent.DEALER_TAB]
        : HeaderComponent.BASE_TABS));
  }

  public onNavigate(item: HeaderNavItem): void {
    this.router.navigate([Tab.MAIN, item.value]).then();
  }

  public onSettings(): void {
    this.dialog.open(SettingsDialogComponent, {
      panelClass: [GlobalStyleClass.DIALOG_SIZE_LIMIT],
      data: {
        user: this.authUserService.getInstant(),
      }
    });
  }

  public onChangePassword(): void {
    this.dialog.open(ChangePasswordDialogComponent, {
      width: '350px',
      data: {
        user: this.authUserService.getInstant()
      }
    });
  }

  public onOpenAboutProgram(): void {
    this.dialog.open(AppVersionComponent, {
      panelClass: [GlobalStyleClass.DIALOG_SIZE_LIMIT]
    });
  }

  public onExit(): void {
    this.authUserService.logout([Tab.MAIN])
  }

  /** Все маршруты админки — /main/<tab> или /main/<tab>/:id, поэтому вкладку задаёт сегмент после main. */
  private static toTab(urlTree: UrlTree): string {
    return urlTree.root.children.primary?.segments[1]?.path;
  }
}
